import React, { useCallback, useEffect, useState } from "react";
import { Box, Chip, FormControlLabel, Grid, LinearProgress, Paper, Switch, Typography } from "@mui/material";
import api from "../../api";
import { toast } from "react-toastify";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import AnalyticsFiltersBar from "./AnalyticsFiltersBar";
import useAnalyticsFilters from "./useAnalyticsFilters";
import AnalyticsDataTable, { StatusChip } from "./AnalyticsDataTable";

const formatDue = (iso) =>
  iso ? new Date(iso).toLocaleString('en-GB', { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—";

function Tile({ label, value, note }) {
  return (
    <Paper sx={{ ...CARD_SX }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>{value ?? "—"}</Typography>
      {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
    </Paper>
  );
}

function SideSplit({ label, split }) {
  const course = split?.course ?? 0;
  const mobile = split?.mobile ?? 0;
  const total = course + mobile;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="body2" fontWeight={600}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">Course {course} · Mobile {mobile}</Typography>
      </Box>
      <LinearProgress variant="determinate" value={total ? (course * 100) / total : 0} sx={{ height: 8, borderRadius: 4, mt: 0.5 }} />
    </Box>
  );
}

/**
 * What each course group has been given (Resource Allocation) and how its students are
 * getting on with it, plus how students split between courses and the mobile plan.
 */
export default function CourseContentAnalytics() {
  const [courseId, setCourseId] = useState("");
  const [academicCourseId, setAcademicCourseId] = useState("");
  const [planId, setPlanId] = useState("");
  const [includeFinished, setIncludeFinished] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const { courses, academicCourses, plans } = useAnalyticsFilters({ courseId, academicCourseId });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (courseId) params.course_id = courseId;
      if (planId) params.course_plan_id = planId;
      if (includeFinished) params.include_finished = 1;
      const res = await api.get("admin/analytics/course-content", { params });
      setData(res.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load course content");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [courseId, planId, includeFinished]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const audience = data?.audience;

  const columns = [
    { key: "group_name", label: "Group", render: (r) => (
      <Box>
        <Typography variant="body2" fontWeight={600}>{r.group_name}</Typography>
        <Typography variant="caption" color="text.secondary">{r.course_name}</Typography>
      </Box>
    ) },
    { key: "format", label: "Type", render: (r) => <Chip size="small" label={r.format === "short" ? "Short (sessions)" : "Long (weeks)"} /> },
    { key: "current_period", label: "Now", render: (r) => (
      <Box>
        <Typography variant="body2">{r.current_period || "Not started"}</Typography>
        <Typography variant="caption" color="text.secondary">{r.periods_released}/{r.periods_total} released</Typography>
        {r.undated_periods > 0 && <Chip size="small" color="warning" label={`${r.undated_periods} undated`} sx={{ ml: 0.5 }} />}
      </Box>
    ) },
    { key: "students", label: "Students" },
    { key: "worksheets_allocated", label: "Worksheets", render: (r) => `${r.worksheets_released}/${r.worksheets_allocated} released` },
    { key: "files_allocated", label: "Files", render: (r) => `${r.files_allocated} course · ${r.tutor_files} tutor` },
    { key: "mock_exams_included", label: "Included", render: (r) => `${r.mock_exams_included} mocks · ${r.past_papers_included} papers` },
    { key: "completion_percent", label: "Completion", render: (r) => (r.completion_percent == null ? "—" : `${r.completion_percent}% (${r.completed}/${r.expected})`) },
    { key: "overdue", label: "Overdue", render: (r) => (r.overdue ? <Chip size="small" color="error" label={r.overdue} /> : 0) },
    { key: "next_deadline", label: "Next deadline", render: (r) => formatDue(r.next_deadline) },
    { key: "status", label: "Status", render: (r) => <StatusChip status={r.status} /> },
  ];

  return (
    <AnalyticsPageShell
      title="Course Content"
      subtitle="What each group has been given, what is released, and how students are keeping up."
      filters={
        <AnalyticsFiltersBar
          courses={courses}
          academicCourses={academicCourses}
          plans={plans}
          courseId={courseId}
          setCourseId={setCourseId}
          showAcademicCourse={false}
          academicCourseId={academicCourseId}
          setAcademicCourseId={setAcademicCourseId}
          planId={planId}
          setPlanId={setPlanId}
        />
      }
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}><Tile label="Groups" value={summary.groups} /></Grid>
        <Grid item xs={12} sm={6} md={3}><Tile label="Worksheets allocated" value={summary.worksheets_allocated} note={`${summary.files_allocated ?? 0} course files`} /></Grid>
        <Grid item xs={12} sm={6} md={3}><Tile label="Completion" value={summary.completion_percent == null ? "—" : `${summary.completion_percent}%`} note="Released worksheets, per student" /></Grid>
        <Grid item xs={12} sm={6} md={3}><Tile label="Overdue" value={summary.overdue} note="Past deadline, not handed in" /></Grid>
      </Grid>

      {audience && (
        <Paper sx={{ ...CARD_SX, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Courses and the mobile plan</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <Chip label={`Course only: ${audience.course_only}`} />
            <Chip label={`Mobile plan only: ${audience.plan_only}`} />
            <Chip color="primary" label={`Course + mobile plan: ${audience.course_and_plan}`} />
          </Box>
          <SideSplit label="Mock exams completed" split={audience.completed_work?.mock_exams} />
          <SideSplit label="Past papers completed" split={audience.completed_work?.past_papers} />
          <SideSplit label="Worksheets completed" split={audience.completed_work?.worksheets} />
        </Paper>
      )}

      <FormControlLabel
        sx={{ mb: 1 }}
        control={<Switch size="small" checked={includeFinished} onChange={(e) => setIncludeFinished(e.target.checked)} />}
        label="Include finished groups"
      />
      <AnalyticsDataTable columns={columns} rows={data?.rows || []} loading={loading} />
    </AnalyticsPageShell>
  );
}
